import React, { useState, useContext, useEffect } from 'react';
import { Button, Dialog } from 'anf-core-react';
import PropTypes from 'prop-types';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import Tmnt from '../../Tmnt/Tmnt';
import useLog from '../../useLog/useLog';
import {
  PAYMENT_CURRENCY_GIFTCARD_REPUDIATE,
  PAYMENT_CURRENCY_PAYMENTMETHOD_REPUDIATE,
  GIFTCARD_CURRENCY_GIFTCARD_RESTRICT,
  MESSAGE_CURRENCY_GIFTCARD_REPUDIATE,
} from '../../../tools/constants';
import { getParamFromUrl, buildLangCurrChangeURI } from '../../../tools/utils';
import replacePlaceholders from '../../../tools/tmntHelpers';

// Map for TMNT keys based on repudiation type
// This is used to render the correct TMNT text in the dialog
const repudiationTMNTSMAP = {
  PAYMENT_CURRENCY_GIFTCARD_REPUDIATE: 'currencyRepudiationGiftCard',
  PAYMENT_CURRENCY_PAYMENTMETHOD_REPUDIATE: 'currencyRepudiationPaymentMethod',
  GLB_REPUDIATION_CURRENCY_GIFTCARD_RESTRICT: 'currencyRepudiationGiftCard',
  MESSAGE_CURRENCY_GIFTCARD_REPUDIATE: 'currencyRepudiationGiftCard',
};

const CURRENCY_REPUDIATION_TYPES = [
  PAYMENT_CURRENCY_GIFTCARD_REPUDIATE,
  PAYMENT_CURRENCY_PAYMENTMETHOD_REPUDIATE,
  GIFTCARD_CURRENCY_GIFTCARD_RESTRICT,
  MESSAGE_CURRENCY_GIFTCARD_REPUDIATE,
];

export default function RepudiationsDialog({ goToPaymentInformationStep }) {
  const { checkoutPageState, updateCheckoutPageState } = useContext(CheckoutPageContext);
  const repudiationData = checkoutPageState?.currencyRepudiation;
  const repudiationType = repudiationData?.repudiationType || '';
  const [isOpen, setIsOpen] = useState(false);
  const logger = useLog('checkout.checkoutPage');

  const closeModal = () => {
    setIsOpen(false);
    // we need to make data-scroll-lock to false till DS team fix the Dialog issue from their end.
    document.documentElement.setAttribute('data-scroll-lock', 'false');
    // clear the currencyRepudiation state
    updateCheckoutPageState({ currencyRepudiation: null });
  };

  const handleSecondaryActionButtonClick = () => {
    closeModal();
    goToPaymentInformationStep();
  };

  useEffect(() => {
    if (CURRENCY_REPUDIATION_TYPES.includes(repudiationType)) {
      setIsOpen(true);
    }
  }, [repudiationData, repudiationType]);

  const renderSecondaryActionButton = () => {
    switch (repudiationType) {
      case PAYMENT_CURRENCY_PAYMENTMETHOD_REPUDIATE:
        return (
          <Button
            variant="tertiary-dark"
            classList="change-payment-button"
            onClick={handleSecondaryActionButtonClick}
          >
            <Tmnt tmnt={checkoutPageState?.textFor?.changePaymentType} />
          </Button>
        );

      case PAYMENT_CURRENCY_GIFTCARD_REPUDIATE:
      case GIFTCARD_CURRENCY_GIFTCARD_RESTRICT:
        return (
          <Button
            variant="tertiary-dark"
            classList="remove-gift-card-button"
            onClick={handleSecondaryActionButtonClick}
          >
            <Tmnt tmnt={checkoutPageState?.textFor?.continueWithoutGiftCard} />
          </Button>
        );
      default:
        return null;
    }
  };

  const handleContinueClick = async () => {
    const storeId = getParamFromUrl('storeId', '11203');
    const catalogId = getParamFromUrl('catalogId', '10901');
    const langId = getParamFromUrl('langId', '-1');
    const country = checkoutPageState?.cart?.shipping?.country ?? 'US';

    const langCurrUrl = buildLangCurrChangeURI({
      storeId, catalogId, langId, country,
    });

    try {
      const response = await fetch(langCurrUrl, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        window.location.reload();
      } else {
        logger.error('Currency change failed:', response.status);
      }
    } catch (error) {
      logger.error('Currency change error:', error);
    }
  };
  const continueInCurrencyLabel = replacePlaceholders(
    checkoutPageState?.textFor?.continueInCurrency?.value || 'Continue in {0}',
    ['USD'],
  );

  return repudiationData ? (
    <Dialog
      isOpen={isOpen}
      onClose={closeModal}
      id="modal-currency-repudiation"
      heading={<h1><Tmnt tmnt={checkoutPageState?.textFor?.currency} /></h1>}
      closeButtonLabel="Close"
      classList="modal-currency-repudiation"
    >
      <div className="currency-repudiation">
        <p
          className="error-message"
          data-property={repudiationData?.error?.key}
        >
          <Tmnt tmnt={repudiationData?.error} />
        </p>
        <p
          className="error-explained"
          data-property={`${repudiationData?.repudiationType}_EXPLAINED`}
        >
          <Tmnt
            tmnt={checkoutPageState?.textFor?.[repudiationTMNTSMAP[repudiationType]]}
          />
        </p>
        <Button isFullWidth variant="secondary" onClick={handleContinueClick}>
          {continueInCurrencyLabel}
        </Button>
        {renderSecondaryActionButton()}
      </div>
    </Dialog>
  ) : null;
}

RepudiationsDialog.defaultProps = {
  goToPaymentInformationStep: () => {},
};

RepudiationsDialog.propTypes = {
  goToPaymentInformationStep: PropTypes.func,
};
